import { ApiError, postJsonWithStatus } from './client';
import { ApiEnvelope, isApiEnvelope } from './migrations';

// FD-321 (FD-ADR-034) — the console half of the brokered provisioning-package
// build. `POST /v1/migrations/{id}/build-ppkg`, added by FD-319.
//
// ── WHAT THIS MODULE MUST NEVER DO ─────────────────────────────────────────
//
// The package embeds the wave's BPRT — a tenant-join credential Rubix cannot
// revoke. The endpoint deliberately returns NON-SECRET STATE ONLY: the bytes are
// never in the response, and there is no field, parameter or header here that
// asks for them. Nothing in the console ever holds the base64. If a future
// change appears to need it, the change is wrong.
//
// ── AND WHY THERE IS NO RETRY IN THIS MODULE ───────────────────────────────
//
// `buildPpkg` makes exactly ONE request, with no interceptor, no react-query
// `retry`, and no interval. That is the whole point of the refusal taxonomy
// below: a `409` took no effect and is refused identically forever, and
// `PPKG_UNKNOWN` means it is NOT KNOWN whether a package was built. Retrying
// either automatically is how a caller manufactures extra copies of an
// unrevocable credential. Retry is an OPERATOR action, offered only for the
// codes that assert nothing happened (or that the build ran and failed).

/**
 * `PpkgStatus` — the three-state vocabulary FD-319 stamps and FD-320 reads.
 *
 * `stored` and `delivered` are BOTH servable to a device. `stored` means the
 * package is verified and in Key Vault but ATC has not confirmed the delivery
 * receipt; the campaign is usable. Rendering it as a problem state would send
 * operators chasing something that is not blocking anything.
 *
 * `building` is also where a FAILED build leaves the row — on a 500 whether a
 * build occurred is unknown, and a `failed` status would claim knowledge the
 * platform does not have.
 */
export type PpkgStatus = 'building' | 'stored' | 'delivered';

/** True for the states a device can actually be served from. */
export function isPpkgServable(status: PpkgStatus | null | undefined): boolean {
  return status === 'stored' || status === 'delivered';
}

/** `POST /v1/migrations/{id}/build-ppkg` — 200 `data`. Non-secret only. */
export interface BuildPpkgResult {
  migrationId: string;
  packageId: string;
  buildId: string;
  /** DECODED length in bytes — not ATC's asserted value. */
  sizeBytes: number;
  /** Lowercase hex SHA-256 over the raw binary, pre-base64. Identifier, not a secret. */
  sha256: string;
  builtUtc: string;
  /** '' while the ack is unconfirmed. */
  ackedUtc: string;
  ppkgStatus: PpkgStatus;
  ackPending: boolean;
  rebuilt: boolean;
  /** Present only when `ackPending` — the server's own sentence about it. */
  warning?: string;
}

/**
 * Builds (or, on an ack-pending row, re-acks) this campaign's package. ONE
 * attempt. Runs ~71 s against a cold Rubix worker, so callers must treat it as
 * a long request rather than assume a fast failure.
 */
export async function buildPpkg(migrationId: string): Promise<BuildPpkgResult> {
  const { body } = await postJsonWithStatus<ApiEnvelope<BuildPpkgResult>, Record<string, never>>(
    `/v1/migrations/${encodeURIComponent(migrationId)}/build-ppkg`,
    {},
  );
  return body.data;
}

/**
 * The machine codes `build-ppkg` returns. Enumerated rather than pattern-matched
 * because the console's job here is to distinguish refusals that demand
 * OPPOSITE operator actions — and two pairs in particular must never share a
 * branch:
 *
 * - `PPKG_RATE_LIMITED` (429) vs `PPKG_WORKER_UNAVAILABLE` (503). Both took no
 *   effect, and both are recoverable by retry — but one means "ask Rubix to
 *   raise the limit" and the other means "page Rubix". Collapsing them
 *   implements the defect the status codes exist to prevent.
 * - `PPKG_UNKNOWN` (502) vs `PPKG_WORKER_UNAVAILABLE` (503). The 503 asserts
 *   nothing happened, so retry is safe by construction. `PPKG_UNKNOWN` asserts
 *   only that ATC could not classify its own failure: a package MAY exist. They
 *   must not share a code path.
 *
 * And two codes point somewhere else entirely:
 *
 * - `BPRT_EXPIRED` / `PPKG_TENANT_BINDING_MISMATCH` — the remedy is
 *   RE-ACTIVATING the wave, not rebuilding. Surfacing either as a build failure
 *   sends the operator to the wrong button.
 * - `REBUILD_LIMIT_EXCEEDED` — NOT capacity. Packages were delivered to this
 *   deployment and never acknowledged. It needs a person, and acking now does
 *   not reopen the build.
 */
const PPKG_ERROR_CODES = [
  // 409 — terminal, took no effect, refused identically forever.
  'PACKAGE_ALREADY_DELIVERED',
  'PACKAGE_ID_CONFLICT',
  'REBUILD_LIMIT_EXCEEDED',
  'PPKG_BUILD_REFUSED',
  'BPRT_NOT_ACTIVE',
  'BPRT_EXPIRED',
  'PPKG_TENANT_BINDING_MISMATCH',
  // 429 / 503 — refused at admission or unavailable; retry is the recovery.
  'PPKG_RATE_LIMITED',
  'PPKG_WORKER_UNAVAILABLE',
  'PPKG_NOT_CONFIGURED',
  // 502 — the build ran and failed, or the answer was not usable.
  'PPKG_BUILD_FAILED',
  'PPKG_UNKNOWN',
  'PPKG_REQUEST_REJECTED',
  'PPKG_AUTH_FAILED',
  'PPKG_PROTOCOL_ERROR',
  'PPKG_HASH_MISMATCH',
  'PPKG_DECODE_FAILED',
  // 500 — local, after or instead of a build.
  'PPKG_TOO_LARGE',
  'PPKG_STORE_FAILED',
  'PPKG_STATE_STAMP_FAILED',
  'PPKG_STATE_INCONSISTENT',
  'PACKAGE_ID_RESERVE_FAILED',
  'DESTINATION_TENANT_UNRESOLVED',
] as const;

export type PpkgErrorCode = (typeof PPKG_ERROR_CODES)[number] | 'UNKNOWN';

const KNOWN_PPKG_ERROR_CODES: ReadonlySet<string> = new Set(PPKG_ERROR_CODES);

export function ppkgErrorCode(err: unknown): PpkgErrorCode {
  if (!(err instanceof ApiError)) return 'UNKNOWN';
  const code = isApiEnvelope(err.body) ? err.body.error?.code : undefined;
  return typeof code === 'string' && KNOWN_PPKG_ERROR_CODES.has(code)
    ? (code as PpkgErrorCode)
    : 'UNKNOWN';
}

/** The server's `Retry-After` window in seconds, off the 429/503 envelope's `data`. */
export function ppkgRetryAfterSeconds(err: unknown): number | undefined {
  if (!(err instanceof ApiError) || !isApiEnvelope<{ retryAfterSeconds?: unknown }>(err.body)) {
    return undefined;
  }
  const value = err.body.data?.retryAfterSeconds;
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;
}

/** Correlation fields a 409 carries. Every one is OPTIONAL — see FD-319. */
export interface PpkgConflictDetail {
  packageId?: string;
  buildId?: string;
  builtUtc?: string;
  ackedUtc?: string;
}

export function ppkgConflictDetail(err: unknown): PpkgConflictDetail {
  if (!(err instanceof ApiError) || !isApiEnvelope<PpkgConflictDetail>(err.body)) return {};
  const d = err.body.data;
  if (!d || typeof d !== 'object') return {};
  const pick = (v: unknown) => (typeof v === 'string' && v.length > 0 ? v : undefined);
  return {
    packageId: pick(d.packageId),
    buildId: pick(d.buildId),
    builtUtc: pick(d.builtUtc),
    ackedUtc: pick(d.ackedUtc),
  };
}

/**
 * Codes for which RETRYING THIS BUILD is the recovery, so the UI may offer a
 * retry button. Deliberately an ALLOW-LIST rather than a deny-list: a code
 * nobody has classified yet must default to "no automatic retry offered",
 * because the failure mode of guessing wrong is an extra copy of an unrevocable
 * credential.
 *
 * Excluded on purpose, and each for its own reason:
 *  - every 409 — refused identically forever; retry cannot change it
 *  - `PPKG_UNKNOWN` — a package MAY already exist
 *  - `PPKG_NOT_CONFIGURED` — there is nothing to retry until an app setting changes
 *  - `PPKG_AUTH_FAILED` / `PPKG_REQUEST_REJECTED` / `PPKG_PROTOCOL_ERROR` /
 *    `PPKG_HASH_MISMATCH` / `PPKG_DECODE_FAILED` — a defect, not a transient
 *  - `PPKG_TOO_LARGE` — the same bytes will be too large again
 *
 * `PPKG_RATE_LIMITED` is retryable but only after its window, which is why the
 * caller must gate the button on `ppkgRetryAfterSeconds`.
 */
const OPERATOR_RETRYABLE: ReadonlySet<PpkgErrorCode> = new Set<PpkgErrorCode>([
  'PPKG_WORKER_UNAVAILABLE',
  'PPKG_RATE_LIMITED',
  'PPKG_BUILD_FAILED',
  'PPKG_STORE_FAILED',
  'PPKG_STATE_STAMP_FAILED',
  'PACKAGE_ID_RESERVE_FAILED',
]);

export function isOperatorRetryablePpkgError(err: unknown): boolean {
  return OPERATOR_RETRYABLE.has(ppkgErrorCode(err));
}
